import { useEffect, useState, useCallback } from "react";
import { Plus, Trash, PencilSimple, Receipt, ArrowDownLeft, ArrowUpRight, FileXls, FilePdf, Coins, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { downloadFile, formatApiErrorDetail } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { formatMoney, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const empty = { name: "", type: "customer", currency: "TRY", balance: "", email: "", phone: "", note: "" };
const NONE = "__none__";

export default function Contacts() {
  const { currencies } = useCurrency();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [filter, setFilter] = useState("all");
  const [stmt, setStmt] = useState(null);
  const [stmtOpen, setStmtOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [payData, setPayData] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [editPartial, setEditPartial] = useState({ id: null, value: "", date: "" });
  const [addPay, setAddPay] = useState({ id: null, amount: "", account_id: "" });

  const openStatement = async (c) => {
    setStmt(null);
    setStmtOpen(true);
    try {
      const { data } = await api.get(`/contacts/${c.id}/statement`);
      setStmt(data);
    } catch (e) { toast.error("Ekstre yüklenemedi"); }
  };

  const openPayments = async (c) => {
    setPayData(null);
    setEditPartial({ id: null, value: "", date: "" });
    setAddPay({ id: null, amount: "", account_id: "" });
    setPayOpen(true);
    try {
      const { data } = await api.get(`/contacts/${c.id}/payments`);
      setPayData(data);
    } catch (e) { toast.error("Ödemeler yüklenemedi"); }
  };
  const reloadPayments = async () => {
    if (!payData?.contact?.id) return;
    try {
      const { data } = await api.get(`/contacts/${payData.contact.id}/payments`);
      setPayData(data);
    } catch (e) {}
    load();
  };
  const saveEditPartial = async (txId) => {
    const v = Number(editPartial.value);
    if (!v || v <= 0) return toast.error("Geçerli tutar girin");
    try {
      await api.put(`/payments/partial/${txId}`, { amount: v, date: editPartial.date || null });
      toast.success("Kısmi ödeme güncellendi");
      setEditPartial({ id: null, value: "", date: "" });
      reloadPayments();
    } catch (e) { toast.error("Güncellenemedi"); }
  };
  const deletePartial = async (txId) => {
    try {
      await api.delete(`/payments/partial/${txId}`);
      toast.success("Kısmi ödeme silindi");
      reloadPayments();
    } catch (e) { toast.error("Silinemedi"); }
  };
  const submitAddPartial = async (paymentId) => {
    const v = Number(addPay.amount);
    if (!v || v <= 0) return toast.error("Geçerli tutar girin");
    try {
      await api.post(`/payments/${paymentId}/pay-partial`, { amount: v, account_id: addPay.account_id || null });
      toast.success("Kısmi ödeme eklendi");
      setAddPay({ id: null, amount: "", account_id: "" });
      reloadPayments();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eklenemedi"); }
  };
  const exportContactStatement = (fmt) => {
    if (!payData?.contact?.id) return;
    const ext = fmt === "pdf" ? "pdf" : "xlsx";
    downloadFile(`/contacts/${payData.contact.id}/payments/export?format=${fmt}`, `cari-ekstre.${ext}`).catch(() => toast.error("Dışa aktarılamadı"));
  };

  const load = useCallback(() => {
    api.get("/contacts").then((r) => setItems(r.data)).catch(() => {});
    api.get("/accounts").then((r) => setAccounts(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...c, balance: String(c.balance) }); setEditId(c.id); setOpen(true); };

  const submit = async () => {
    if (!form.name) return toast.error("İsim girin");
    const payload = { ...form, balance: Number(form.balance) || 0 };
    try {
      if (editId) await api.put(`/contacts/${editId}`, payload);
      else await api.post("/contacts", payload);
      toast.success(editId ? "Cari güncellendi" : "Cari eklendi");
      setOpen(false);
      load();
    } catch (e) { toast.error("Kaydedilemedi"); }
  };

  const remove = async (id) => { await api.delete(`/contacts/${id}`); toast.success("Silindi"); load(); };

  const [expRange, setExpRange] = useState({ start: "", end: "" });
  const exportSummary = (fmt) => {
    const ext = fmt === "pdf" ? "pdf" : "xlsx";
    const params = new URLSearchParams({ format: fmt });
    if (expRange.start) params.set("start", expRange.start);
    if (expRange.end) params.set("end", expRange.end);
    downloadFile(`/contacts/export?${params.toString()}`, `cari-ozet.${ext}`).catch(() => toast.error("Dışa aktarılamadı"));
  };

  const filtered = items.filter((i) => filter === "all" || i.type === filter);

  return (
    <div className="space-y-6 animate-fade-in-up">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-light tracking-tight">Cari Hesaplar</h1>
          <p className="text-sm text-muted-foreground mt-1">Müşteri ve tedarikçi bakiyelerini takip edin</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Vade Başl.</Label>
            <Input type="date" value={expRange.start} onChange={(e) => setExpRange((r) => ({ ...r, start: e.target.value }))} className="mt-1 h-9 w-36 bg-secondary border-border text-xs" data-testid="contacts-export-start" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-muted-foreground">Vade Bitiş</Label>
            <Input type="date" value={expRange.end} onChange={(e) => setExpRange((r) => ({ ...r, end: e.target.value }))} className="mt-1 h-9 w-36 bg-secondary border-border text-xs" data-testid="contacts-export-end" />
          </div>
          <Button onClick={() => exportSummary("xlsx")} data-testid="contacts-export-xlsx" variant="outline" className="border-border hover:bg-secondary">
            <FileXls size={18} className="mr-1" /> Excel
          </Button>
          <Button onClick={() => exportSummary("pdf")} data-testid="contacts-export-pdf" variant="outline" className="border-border hover:bg-secondary">
            <FilePdf size={18} className="mr-1" /> PDF
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="add-contact-btn" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Plus size={18} className="mr-1" /> Yeni Cari
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader><DialogTitle className="font-heading">{editId ? "Cari Düzenle" : "Yeni Cari Hesap"}</DialogTitle>
              <DialogDescription className="text-muted-foreground">Müşteri veya tedarikçi bilgilerini girin.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Ünvan / İsim</Label>
                  <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="contact-name" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Tür</Label>
                  <Select value={form.type} onValueChange={(v) => set("type", v)}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="contact-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="customer">Müşteri</SelectItem>
                      <SelectItem value="supplier">Tedarikçi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Bakiye (+ alacak / - borç)</Label>
                  <Input type="number" value={form.balance} onChange={(e) => set("balance", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="contact-balance" placeholder="0" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Para Birimi</Label>
                  <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                    <SelectTrigger className="mt-1.5 bg-secondary border-border" data-testid="contact-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">E-posta</Label>
                  <Input value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="contact-email" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-muted-foreground">Telefon</Label>
                  <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5 bg-secondary border-border" data-testid="contact-phone" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} data-testid="contact-save" className="bg-primary text-primary-foreground hover:bg-primary/90">Kaydet</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="flex gap-2">
        {[["all", "Tümü"], ["customer", "Müşteriler"], ["supplier", "Tedarikçiler"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`contact-filter-${v}`}
            className={`px-4 py-1.5 rounded-md text-sm transition-colors ${filter === v ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Ünvan</TableHead>
              <TableHead>Tür</TableHead>
              <TableHead className="text-right">Toplam</TableHead>
              <TableHead className="text-right">Ödenen</TableHead>
              <TableHead className="text-right">Kalan</TableHead>
              <TableHead className="text-right">Bakiye</TableHead>
              <TableHead className="w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Cari bulunamadı.</TableCell></TableRow>
            ) : filtered.map((c) => (
              <TableRow key={c.id} className="border-border" data-testid={`contact-row-${c.id}`}>
                <TableCell className="font-medium">
                  {c.name}
                  {c.created_by_name && (
                    <div className="mt-1">
                      <Badge variant="outline" data-testid={`contact-enteredby-${c.id}`}
                        className={c.created_by_role === "staff" ? "border-primary/40 text-primary text-[10px]" : "border-border text-muted-foreground text-[10px]"}>
                        {c.created_by_role === "staff" ? "👤 " : ""}{c.created_by_name}
                      </Badge>
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="border-border text-muted-foreground">
                    {c.type === "customer" ? "Müşteri" : "Tedarikçi"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right font-mono-num text-sm" data-testid={`contact-total-${c.id}`}>
                  {c.billed_total ? formatMoney(c.billed_total, c.currency, currencies) : "-"}
                </TableCell>
                <TableCell className="text-right font-mono-num text-sm text-[#10B981]" data-testid={`contact-paid-${c.id}`}>
                  {c.billed_total ? formatMoney(c.billed_paid, c.currency, currencies) : "-"}
                </TableCell>
                <TableCell className={`text-right font-mono-num text-sm font-medium ${c.billed_remaining > 0 ? "text-[#EF4444]" : "text-muted-foreground"}`} data-testid={`contact-remaining-${c.id}`}>
                  {c.billed_total ? formatMoney(c.billed_remaining, c.currency, currencies) : "-"}
                </TableCell>
                <TableCell className={`text-right font-mono-num font-medium ${c.balance >= 0 ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                  {formatMoney(c.balance, c.currency, currencies)}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => openPayments(c)} data-testid={`contact-payments-${c.id}`} className="text-muted-foreground hover:text-primary transition-colors" title="Ödemeler"><Coins size={16} /></button>
                    <button onClick={() => openStatement(c)} data-testid={`contact-statement-${c.id}`} className="text-muted-foreground hover:text-primary transition-colors" title="Ekstre"><Receipt size={16} /></button>
                    <button onClick={() => openEdit(c)} data-testid={`contact-edit-${c.id}`} className="text-muted-foreground hover:text-primary transition-colors"><PencilSimple size={16} /></button>
                    <ConfirmDelete onConfirm={() => remove(c.id)} testId={`contact-delete-${c.id}`}
                      className="text-muted-foreground hover:text-destructive transition-colors"
                      title="Cariyi sil?" description={`"${c.name}" carisi silinecek. Bu işlem geri alınamaz.`}>
                      <Trash size={16} />
                    </ConfirmDelete>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={stmtOpen} onOpenChange={setStmtOpen}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Cari Ekstresi{stmt ? ` — ${stmt.contact.name}` : ""}</DialogTitle>
            <DialogDescription className="text-muted-foreground">Bu cariye ait tüm gelir, gider, ödeme ve tahsilat hareketleri.</DialogDescription>
          </DialogHeader>
          {!stmt ? (
            <div className="py-10 flex justify-center">
              <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="border border-border rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Güncel Bakiye</div>
                  <div className={`font-mono-num font-semibold mt-1 ${stmt.contact.balance >= 0 ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                    {formatMoney(stmt.contact.balance, stmt.contact.currency, currencies)}
                  </div>
                </div>
                <div className="border border-border rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Toplam Giren</div>
                  <div className="font-mono-num font-semibold mt-1 text-[#10B981]">{formatMoney(stmt.total_in, stmt.contact.currency, currencies)}</div>
                </div>
                <div className="border border-border rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Toplam Çıkan</div>
                  <div className="font-mono-num font-semibold mt-1 text-[#EF4444]">{formatMoney(stmt.total_out, stmt.contact.currency, currencies)}</div>
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto border border-border rounded-md divide-y divide-border" data-testid="statement-movements">
                {stmt.movements.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-6 text-center">Bu cariye ait hareket bulunamadı.</p>
                ) : stmt.movements.map((m, i) => (
                  <div key={i} className="flex items-center gap-3 p-3">
                    <div className={`h-8 w-8 rounded-md flex items-center justify-center ${m.direction === "in" ? "bg-success/10" : "bg-destructive/10"}`}>
                      {m.direction === "in" ? <ArrowDownLeft size={16} className="text-[#10B981]" /> : <ArrowUpRight size={16} className="text-[#EF4444]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{m.label}</div>
                      <div className="text-xs text-muted-foreground">{formatDate(m.date)} · {m.status === "pending" ? "Bekliyor" : "Tamamlandı"}</div>
                    </div>
                    <div className={`font-mono-num font-medium ${m.direction === "in" ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                      {m.direction === "in" ? "+" : "-"}{formatMoney(m.amount, m.currency, currencies)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">Cari Ödemeleri{payData ? ` — ${payData.contact.name}` : ""}</DialogTitle>
            <DialogDescription className="text-muted-foreground">Bu cariye ait ödeme/tahsilatlar ve yapılan kısmi ödemeler. Kısmi ödemeleri düzenleyebilir veya silebilirsiniz.</DialogDescription>
          </DialogHeader>
          {payData && payData.payments.length > 0 && (
            <div className="flex gap-2 -mt-1">
              <Button onClick={() => exportContactStatement("xlsx")} data-testid="contact-stmt-xlsx" variant="outline" size="sm" className="border-border hover:bg-secondary"><FileXls size={15} className="mr-1" /> Excel</Button>
              <Button onClick={() => exportContactStatement("pdf")} data-testid="contact-stmt-pdf" variant="outline" size="sm" className="border-border hover:bg-secondary"><FilePdf size={15} className="mr-1" /> PDF</Button>
            </div>
          )}
          {!payData ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Yükleniyor...</div>
          ) : payData.payments.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground" data-testid="contact-payments-empty">Bu cariye bağlı ödeme/tahsilat yok.</div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto space-y-3" data-testid="contact-payments-list">
              {payData.payments.map((p) => {
                const overdue = p.status !== "paid" && p.remaining > 0 && p.due_date && new Date(p.due_date) < new Date(new Date().toDateString());
                return (
                <div key={p.id} data-testid={`cpay-${p.id}`} className={`border rounded-md p-3 ${overdue ? "border-destructive/50 bg-destructive/5" : "border-border bg-secondary/20"}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={`h-8 w-8 rounded-md flex items-center justify-center shrink-0 ${p.type === "receivable" ? "bg-success/10" : "bg-destructive/10"}`}>
                        {p.type === "receivable" ? <ArrowDownLeft size={16} className="text-[#10B981]" /> : <ArrowUpRight size={16} className="text-[#EF4444]" />}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{p.type === "receivable" ? "Tahsilat" : "Ödeme"} · {p.description || "-"}</div>
                        <div className={`text-xs ${overdue ? "text-destructive font-medium" : "text-muted-foreground"}`}>Vade {formatDate(p.due_date)}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {overdue && <Badge data-testid={`cpay-overdue-${p.id}`} variant="outline" className="border-destructive/50 text-destructive text-[10px]">Vadesi Geçti</Badge>}
                      <Badge variant="outline" className={p.status === "paid" ? "border-[#10B981]/40 text-[#10B981] text-[10px]" : "border-amber-500/40 text-amber-400 text-[10px]"}>
                        {p.status === "paid" ? "Kapandı" : "Bekliyor"}
                      </Badge>
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                    <div><span className="text-muted-foreground">Toplam</span><div className="font-mono-num">{formatMoney(p.amount, p.currency, currencies)}</div></div>
                    <div><span className="text-muted-foreground">Ödenen</span><div className="font-mono-num text-[#10B981]">{formatMoney(p.paid_amount || 0, p.currency, currencies)}</div></div>
                    <div><span className="text-muted-foreground">Kalan</span><div className={`font-mono-num font-medium ${overdue ? "text-destructive" : "text-[#EF4444]"}`}>{formatMoney(p.remaining, p.currency, currencies)}</div></div>
                  </div>
                  {p.partials && p.partials.length > 0 && (
                    <div className="mt-3 border-t border-border/60 pt-2 space-y-1.5">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Yapılan Kısmi Ödemeler</div>
                      {p.partials.map((t) => (
                        <div key={t.id} data-testid={`partial-${t.id}`} className="flex items-center gap-2 text-sm">
                          <span className="text-xs text-muted-foreground w-20 shrink-0">{formatDate(t.date)}</span>
                          {editPartial.id === t.id ? (
                            <>
                              <input type="number" value={editPartial.value} onChange={(e) => setEditPartial((s) => ({ ...s, value: e.target.value }))}
                                data-testid={`partial-input-${t.id}`}
                                className="flex-1 bg-secondary border border-border rounded px-2 py-1 text-sm font-mono-num" />
                              <input type="date" value={editPartial.date} onChange={(e) => setEditPartial((s) => ({ ...s, date: e.target.value }))}
                                data-testid={`partial-date-${t.id}`}
                                className="bg-secondary border border-border rounded px-2 py-1 text-xs" />
                              <button onClick={() => saveEditPartial(t.id)} data-testid={`partial-save-${t.id}`} className="text-[#10B981] hover:opacity-80 p-1" title="Kaydet"><Check size={16} /></button>
                              <button onClick={() => setEditPartial({ id: null, value: "", date: "" })} data-testid={`partial-cancel-${t.id}`} className="text-muted-foreground hover:text-foreground p-1" title="Vazgeç"><X size={16} /></button>
                            </>
                          ) : (
                            <>
                              <span className="flex-1 font-mono-num">{formatMoney(t.amount, t.currency, currencies)}</span>
                              <button onClick={() => setEditPartial({ id: t.id, value: String(t.amount), date: (t.date || "").slice(0, 10) })} data-testid={`partial-edit-${t.id}`} className="text-muted-foreground hover:text-primary p-1" title="Düzenle"><PencilSimple size={15} /></button>
                              <ConfirmDelete onConfirm={() => deletePartial(t.id)} testId={`partial-delete-${t.id}`}
                                title="Kısmi ödemeyi sil?" description="Bu kısmi ödeme silinecek; bağlı hesap bakiyesi ve kalan tutar güncellenecek.">
                                <Trash size={15} />
                              </ConfirmDelete>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {p.status !== "paid" && (
                    <div className="mt-3 border-t border-border/60 pt-2">
                      {addPay.id === p.id ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <input type="number" value={addPay.amount} onChange={(e) => setAddPay((s) => ({ ...s, amount: e.target.value }))}
                            placeholder={`Tutar (kalan ${formatMoney(p.remaining, p.currency, currencies)})`}
                            data-testid={`addpartial-amount-${p.id}`}
                            className="flex-1 min-w-[140px] bg-secondary border border-border rounded px-2 py-1.5 text-sm font-mono-num" />
                          <Select value={addPay.account_id || NONE} onValueChange={(v) => setAddPay((s) => ({ ...s, account_id: v === NONE ? "" : v }))}>
                            <SelectTrigger className="w-40 h-9 bg-secondary border-border text-xs" data-testid={`addpartial-account-${p.id}`}><SelectValue placeholder="Hesap" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value={NONE}>— Hesap yok —</SelectItem>
                              {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.currency}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Button onClick={() => submitAddPartial(p.id)} data-testid={`addpartial-save-${p.id}`} size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 h-9"><Check size={15} className="mr-1" /> Ekle</Button>
                          <button onClick={() => setAddPay({ id: null, amount: "", account_id: "" })} data-testid={`addpartial-cancel-${p.id}`} className="text-muted-foreground hover:text-foreground p-1"><X size={16} /></button>
                        </div>
                      ) : (
                        <button onClick={() => setAddPay({ id: p.id, amount: "", account_id: p.account_id || "" })} data-testid={`addpartial-open-${p.id}`}
                          className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline">
                          <Coins size={14} /> Kısmi Ödeme Ekle
                        </button>
                      )}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
